/**
 * Zustand slice for AutoCAD session-level system variables (Dynamic
 * Input, drawing aids, object-snap bitmask, etc.).
 *
 * Architectural notes:
 *  - Intentionally NOT merged into `editorStore` — editorStore is
 *    transactional (undo / redo), sysvars are session-level prefs and
 *    must survive undo without polluting the command history.
 *  - Persistence is split into `persist.ts` so the pure schema / store
 *    layer has no `window` imports and is trivially test-mockable.
 *  - `tempOverride` holds transient flags for the Temporary Overrides
 *    feature (Shift = temp ORTHO, Shift+A/S/D/X = temp OSNAP) that must
 *    NOT be persisted. Geometry code reads `selectEffectiveOrtho` etc.
 *    and never peeks at the underlying ORTHOMODE directly.
 */

import { create } from "zustand";

import {
  DYN_DEFAULTS,
  isValidSysvarValue,
  isSysvarKey,
  type SysvarKey,
  type SysvarValues,
} from "./sysvarSchema";
import { loadPersistedSysvars, persistSysvars } from "./persist";

/**
 * Transient override layer for drawing-aid modifiers. A `null` entry
 * means "no override active, fall through to the base sysvar value".
 * A boolean or number overrides for the duration of the modifier
 * keypress (cleared on keyup by sysvarHotkeys).
 */
export type TempOverride = {
  ortho: boolean | null;
  polar: boolean | null;
  osnapMode: number | null;
};

/**
 * OSNAP-OVERRIDE-MODES — transient state of the Extended (Apparent) Intersection
 * override (ТЗ §8.6). Structurally identical to
 * `scene3d/snap/osnapTypes.ExtendedOverrideState` — intentionally re-declared
 * here (not imported) so the low-level `dynamicInput` store never gains an edge
 * into `scene3d` (ПРИОРИТЕТ #2 dependency-graph red zone). `useOsnapBindings`
 * reads this field and passes it to the resolver, where TypeScript structural
 * typing unifies the two shapes.
 *
 *   - `mode` — which override is armed (`extint` / `extappint`).
 *   - `firstOwnerId` — id of the FIRST picked object, `null` until captured.
 *
 * Kept SEPARATE from {@link TempOverride}: the Shift temp-overrides are cleared
 * on Shift keyup, whereas the deferred extended override has its own per-pick
 * lifecycle and must survive Shift release.
 */
export type OsnapExtendedOverride = {
  readonly mode: "extint" | "extappint";
  readonly firstOwnerId: string | null;
} | null;

/**
 * CC-1e P2-2 — origin of the most recent `values` write. The drawing-aid
 * telemetry bridge (`sysvarToggleTelemetryBridge`) reads this to emit a
 * `ui-toggle` event ONLY for user-driven toggles ("user") and SKIP the
 * programmatic DXF-header hydration writes ("hydrate"), which would otherwise
 * be falsely reported as "the user toggled the aid". Every action that mutates
 * `values` stamps its origin so the bridge can tell them apart.
 */
export type SysvarWriteSource = "user" | "hydrate";

export interface SysvarStoreState {
  values: SysvarValues;
  tempOverride: TempOverride;
  /**
   * OSNAP-OVERRIDE-MODES — the live Extended (Apparent) Intersection override
   * (transient, never persisted). `null` when no extended override is armed.
   */
  osnapExtendedOverride: OsnapExtendedOverride;
  /**
   * CC-1e P2-2 — origin of the last `values` mutation ("user" for genuine
   * F-key / SETVAR / status-bar / reset writes, "hydrate" for DXF-header
   * hydration). Read by the toggle telemetry bridge to suppress false
   * "user toggled" emissions on DXF load. Defaults to "user".
   */
  lastWriteSource: SysvarWriteSource;
  /** Write a single sysvar. Returns false if the candidate value fails schema validation. */
  setVar: <K extends SysvarKey>(k: K, v: number) => boolean;
  /** Merge a partial temp override on top of the current one. Values of `null` clear that key. */
  setTempOverride: (patch: Partial<TempOverride>) => void;
  /** Reset all three temp overrides back to `null`. */
  clearTempOverride: () => void;
  /**
   * OSNAP-OVERRIDE-MODES — arm / update / clear the Extended (Apparent)
   * Intersection override. Pass `{ mode, firstOwnerId: null }` to arm (running
   * snaps still resolve until a first operand is captured), the same object with
   * `firstOwnerId` set to capture the first operand, or `null` to clear.
   */
  setOsnapExtendedOverride: (next: OsnapExtendedOverride) => void;
  /** Merge a record coming from DXF HEADER hydration. Unknown / invalid entries are silently dropped. */
  hydrateFromDxf: (partial: Partial<SysvarValues>) => void;
  /** Reset every sysvar to DYN_DEFAULTS (used by audit tests and CLAUDE.md "reset to defaults" command). */
  resetToDefaults: () => void;
}

function initialValues(): SysvarValues {
  return { ...DYN_DEFAULTS, ...loadPersistedSysvars() };
}

/**
 * POLARMODE bit 0 — this app's polar-tracking MASTER "on" switch. Every enable
 * path writes it (F10 in sysvarHotkeys, the DSETTINGS "Polar Tracking On"
 * checkbox), so "polar is on" is `(POLARMODE & 1) !== 0`, NOT `POLARMODE !== 0`.
 * The other POLARMODE bits are CONFIG only — 2 (OST uses polar angles), 4
 * (POLARADDANG additional angles), 8 (SHIFT to acquire OST), 16 (N6
 * app-extension relative-basis, {@link POLARMODE_RELATIVE_BASIS_BIT}) — and
 * must NOT by themselves enable polar tracking or stand ORTHO down. Round-2
 * added bit 4 to POLARMODE but left the on-predicate at `!== 0`, so ticking
 * "Additional angles" with the master off silently polar-locked the cursor
 * and cleared ORTHO (#492 @codex P2). AutoCAD keeps polar-on separate
 * (AUTOSNAP bit 8) and reserves POLARMODE bit 0 for relative-vs-absolute
 * measurement; that real bit is therefore unavailable here, which is why N6
 * gave relative basis its own bit 16 instead ({@link
 * POLARMODE_RELATIVE_BASIS_BIT}'s own doc has the full rationale) — bit 0
 * doubles as the master enable here, consistent with the existing write path.
 */
export const POLARMODE_TRACKING_ON_BIT = 1;

export const useSysvarStore = create<SysvarStoreState>((set, get) => ({
  values: initialValues(),
  tempOverride: { ortho: null, polar: null, osnapMode: null },
  osnapExtendedOverride: null,
  // CC-1e P2-2 — neutral "user" default so install (no write yet) never makes
  // the telemetry bridge mis-classify the first genuine toggle as hydration.
  lastWriteSource: "user",
  setVar: (k, v) => {
    if (!isValidSysvarValue(k, v)) return false;
    const next = { ...get().values, [k]: v } as SysvarValues;
    // AutoCAD ORTHO ↔ POLAR mutual exclusion is a STATE TRANSITION: enabling one
    // drawing aid DISABLES the other (not a passive runtime tie-break). Enforce it
    // at the single setVar choke so EVERY enable path — F8 / F10, the DSETTINGS
    // "enable" checkbox, SETVAR, the status-bar buttons — clears the sibling aid
    // consistently (#492 @codex P2). Previously only `polarDraftLock` stood polar
    // down at READ time, leaving both sysvars set and the cursor hard-orthogonal.
    // POLAR's enable is its MASTER bit 0 only — writing a config-only POLARMODE
    // bit (2/4/8/16, e.g. ticking "Additional angles" while the master is off)
    // is NOT an enable and must leave ORTHO untouched (#492 @codex P2). Disabling one
    // never touches the other. (Temp Shift-overrides are handled separately, so
    // the read-time ortho guard in polarDraftLock still covers the momentary
    // both-effective corner.)
    if (k === "POLARMODE" && (v & POLARMODE_TRACKING_ON_BIT) !== 0)
      next.ORTHOMODE = 0;
    // Enabling ORTHO stands polar tracking down, but must clear ONLY the polar
    // MASTER bit — the config-only bits (POLARADDANG bit 4, OST bits 2/8) must
    // survive so pressing F8 before later re-enabling polar never wipes the
    // additional-angle setup (POLARMODE=4 → 0 was the bug). ORTHOMODE itself has
    // no config bits, so the enable-POLAR branch above still clears it wholesale
    // (#492 @codex P2).
    else if (k === "ORTHOMODE" && v !== 0)
      next.POLARMODE = next.POLARMODE & ~POLARMODE_TRACKING_ON_BIT;
    // User-driven write (F-key / SETVAR / status bar) — telemetry-eligible.
    set({ values: next, lastWriteSource: "user" });
    persistSysvars(next);
    return true;
  },
  setTempOverride: (patch) =>
    set((s) => ({ tempOverride: { ...s.tempOverride, ...patch } })),
  clearTempOverride: () =>
    set({ tempOverride: { ortho: null, polar: null, osnapMode: null } }),
  setOsnapExtendedOverride: (next) => set({ osnapExtendedOverride: next }),
  hydrateFromDxf: (partial) => {
    const filtered: Partial<SysvarValues> = {};
    for (const [k, v] of Object.entries(partial)) {
      if (!isSysvarKey(k)) continue;
      if (typeof v !== "number") continue;
      if (!isValidSysvarValue(k, v)) continue;
      filtered[k] = v;
    }
    const next = { ...get().values, ...filtered } as SysvarValues;
    // CC-1e P2-2 — programmatic DXF-header write; the toggle telemetry bridge
    // skips this so a DXF load is never reported as a user toggle.
    set({ values: next, lastWriteSource: "hydrate" });
    persistSysvars(next);
  },
  resetToDefaults: () => {
    const next: SysvarValues = { ...DYN_DEFAULTS };
    // "Reset to defaults" is a user/command action — keep it telemetry-eligible
    // (only DXF hydration is suppressed).
    set({ values: next, lastWriteSource: "user" });
    persistSysvars(next);
  },
}));

// ── Pure selectors (import and pass into `useSysvarStore(selector)`) ──

/** DYNMODE bit 1 = pointer input enabled. */
export const selectPointerEnabled = (s: SysvarStoreState): boolean =>
  (s.values.DYNMODE & 1) !== 0;

/** DYNMODE bit 2 = dimension input enabled. */
export const selectDimEnabled = (s: SysvarStoreState): boolean =>
  (s.values.DYNMODE & 2) !== 0;

/** DYNPICOORDS: 0 → polar (distance/angle), 1 → cartesian (X/Y). */
export const selectCoordsMode = (
  s: SysvarStoreState,
): "polar" | "cartesian" =>
  s.values.DYNPICOORDS === 0 ? "polar" : "cartesian";

/** DYNPIFORMAT: 0 → relative (@), 1 → absolute (#). */
export const selectInputFormat = (
  s: SysvarStoreState,
): "relative" | "absolute" =>
  s.values.DYNPIFORMAT === 0 ? "relative" : "absolute";

/** Effective ORTHO state = temp override if set, else ORTHOMODE. */
export const selectEffectiveOrtho = (s: SysvarStoreState): boolean => {
  const base = s.values.ORTHOMODE !== 0;
  return s.tempOverride.ortho === null ? base : s.tempOverride.ortho;
};

/**
 * Effective POLAR state = temp override if set, else the POLARMODE master bit.
 * "Polar on" is the master bit 0 (POLARMODE_TRACKING_ON_BIT), NOT `!== 0`: a
 * config-only bit like POLARADDANG (4) must not by itself make the cursor
 * polar-lock while the master toggle is off (#492 @codex P2).
 */
export const selectEffectivePolar = (s: SysvarStoreState): boolean => {
  const base = (s.values.POLARMODE & POLARMODE_TRACKING_ON_BIT) !== 0;
  return s.tempOverride.polar === null ? base : s.tempOverride.polar;
};

/**
 * POLARMODE bit 4 (Autodesk POLARMODE reference) — "use the additional polar
 * tracking angles (POLARADDANG)". This bit is the SINGLE source of truth for
 * whether the user-defined angle list participates: the DSETTINGS "Additional
 * angles" checkbox writes it and the polar draft-lock reads it, so the setting
 * round-trips through `SETVAR POLARMODE` / query (#492 @codex P2 — it used to
 * live only as a redundant boolean in polarTrackingPrefs that never reached the
 * sysvar). The angle LIST itself stays the `POLARADDANG` string store.
 */
export const POLARMODE_ADDITIONAL_ANGLES_BIT = 4;

/** Whether POLARMODE requests the additional polar angles (POLARADDANG, bit 4). */
export const selectPolarAddAngEnabled = (s: SysvarStoreState): boolean =>
  (s.values.POLARMODE & POLARMODE_ADDITIONAL_ANGLES_BIT) !== 0;

/**
 * N6 (POLAR-RELATIVE-BASIS-WIRE) — APP-EXTENSION POLARMODE bit for "measure
 * polar angles relative to the last segment drawn" (AutoCAD's real
 * Absolute/Relative Polar Angle Measurement radio group in the Drafting
 * Settings "Polar Tracking" tab).
 *
 * In real AutoCAD this is documented POLARMODE bit VALUE 1 — but in this
 * app's model bit 1 is already {@link POLARMODE_TRACKING_ON_BIT}, the
 * polar-tracking master on/off switch (a #492 deviation, made because
 * AutoCAD itself exposes no documented on/off GETVAR to reuse instead — see
 * that constant's own doc). Reusing bit 1 for BOTH concerns would collapse
 * them into one: "relative" would then be true if-and-only-if polar tracking
 * is on, making Absolute-while-polar-on unrepresentable, which is not what
 * the AutoCAD dialog offers (Absolute/Relative is independently selectable
 * whether or not Polar Tracking is currently on). Bit 16 is therefore an
 * APP-ONLY extension slot — AutoCAD's own POLARMODE bitcode never documents
 * anything past bit 8, so this does not collide with any real AutoCAD
 * semantics, only with this app's own already-shipped bit-1 repurposing.
 *
 * `polarTrackingPrefs.ts` used to carry a same-named `angleBasis` field that
 * nothing ever wrote (the MASTERPLAN N6 gap) — removed in favour of this bit
 * being the SINGLE source of truth, mirroring the {@link
 * POLARMODE_ADDITIONAL_ANGLES_BIT} precedent (config bits live in the sysvar
 * for SETVAR parity; only non-numeric data, like the POLARADDANG angle list
 * itself, stays in the prefs store).
 */
export const POLARMODE_RELATIVE_BASIS_BIT = 16;

/**
 * POLARMODE "measure polar angles" basis — "relative" (to the last drawn
 * segment) when bit 16 is set, else AutoCAD's "absolute" (UCS X axis)
 * default. Fed straight into `PolarTrackingConfig.angleBasis`
 * (`scene3d/snap/polarConstraint.ts`) by `polarDraftLock.ts` /
 * `polarGuide.ts` — the single place both readers resolve the live basis.
 *
 * Return type is the `PolarAngleBasis` union re-declared inline (not
 * imported) — same reasoning as {@link OsnapExtendedOverride} above: the
 * low-level `dynamicInput` store must never gain an edge into `scene3d`
 * (ПРИОРИТЕТ #2 dependency-graph red zone; `scene3d/snap/polarDraftLock.ts`
 * already imports FROM this store, so the reverse edge would cycle).
 * TypeScript's structural typing unifies the two identical string-literal
 * unions at every call site.
 */
export const selectPolarAngleBasis = (
  s: SysvarStoreState,
): "absolute" | "relative" =>
  (s.values.POLARMODE & POLARMODE_RELATIVE_BASIS_BIT) !== 0
    ? "relative"
    : "absolute";

/**
 * TRACKPATH bit 2 (Autodesk TRACKPATH reference) — "does not display polar
 * tracking path" (bits 2 and 3 both suppress it; the other bit, value 1,
 * only affects the OSNAP-tracking-path portion this app does not render
 * yet). A pure DISPLAY gate on the dashed alignment-path LINE only:
 * `polarLockedCursor` still locks the cursor position when this is set, AND
 * the "distance < angle°" AutoTrack tooltip stays visible — AutoCAD's
 * TRACKPATH is documented as gating the alignment path, never the tooltip
 * or whether polar tracking itself engages (`resolveDraftAidConstraint`
 * consumes this ONLY to decide whether to populate the rendered
 * `polarGuide` line; `polarTip` is unconditional once polar engages).
 */
export const TRACKPATH_NO_POLAR_PATH_BIT = 2;

/** Whether the polar tracking alignment-path LINE should render (TRACKPATH bit 2 clear). */
export const selectPolarTrackPathVisible = (s: SysvarStoreState): boolean =>
  (s.values.TRACKPATH & TRACKPATH_NO_POLAR_PATH_BIT) === 0;

// ── OTRACK-SUBSYSTEM (object-snap tracking, F11) ──────────────────────

/** Effective OTRACK state = temp override if set, else OTRACKMODE. Mirrors
 * {@link selectEffectiveOrtho}. AutoCAD has no documented Shift temp-override
 * for OTRACK's master switch itself (Shift instead gates ACQUISITION via
 * POLARMODE bit 8 — {@link selectOtrackShiftToAcquire}), so `tempOverride`
 * carries no `otrack` field; this selector reads OTRACKMODE directly. */
export const selectEffectiveOtrack = (s: SysvarStoreState): boolean =>
  s.values.OTRACKMODE !== 0;

/**
 * POLARMODE bit 2 (Autodesk POLARMODE reference) — "object snap tracking
 * uses the polar tracking angles". When set, OTRACK's alignment-path angle
 * table additionally includes the live POLAR angle table (increment +
 * additional angles + basis), not just the four cardinal directions.
 */
export const POLARMODE_OTRACK_USES_POLAR_ANGLES_BIT = 2;

export const selectOtrackUsesPolarAngles = (s: SysvarStoreState): boolean =>
  (s.values.POLARMODE & POLARMODE_OTRACK_USES_POLAR_ANGLES_BIT) !== 0;

/**
 * POLARMODE bit 8 (Autodesk POLARMODE reference) — "press SHIFT to acquire
 * object-snap tracking points". When set, hover-dwell over an OSNAP
 * candidate only ARMS a tracking point while Shift is held; when clear
 * (AutoCAD default), the dwell alone acquires.
 */
export const POLARMODE_OTRACK_SHIFT_TO_ACQUIRE_BIT = 8;

export const selectOtrackShiftToAcquire = (s: SysvarStoreState): boolean =>
  (s.values.POLARMODE & POLARMODE_OTRACK_SHIFT_TO_ACQUIRE_BIT) !== 0;

// ── editor.renderer (PR-A — Three.js + WebGPU rollout) ────────────────
//
// The `editor.renderer` sysvar selects the active rendering back-end:
//   * "svg"   — legacy `<PrimitiveAtlasStage>` (default)
//   * "webgl" — new `<Scene3DStage>` (Three.js / WebGPU)
//
// It lives in a sibling store (`state/rendererPreference.ts`) — the
// numeric AutoCAD-parity sysvar schema above is strictly integer-valued
// (DYNMODE / OSMODE / etc.); mixing a string-typed option into that
// schema would break the AutoCAD `SETVAR` parity contract. Re-exported
// from here so the project-wide convention "sysvars live in sysvarStore"
// continues to hold for grep / discovery purposes.
//
// Persistence: localStorage `edit-dwg.renderer`.
//
// PR-K (2026-05-05): Q2 user decision flipped default to "webgl" and
// removed the `?renderer=svg|webgl` URL override path. Rollback strategy
// is `git revert` of the PR-K commit, not a runtime flag.
// `applyUrlRendererOverride()` retained as a deprecated no-op shim for
// import-stability; remove after one release.
export {
  useRendererPreference,
  getRendererPreference,
  applyUrlRendererOverride,
  DEFAULT_RENDERER_PREFERENCE,
  type RendererBackendChoice,
  type RendererPreference,
} from "../state/rendererPreference";
